import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/providers/AuthProvider';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  href?: string;
}

interface OnboardingContextType {
  currentStep: number;
  steps: OnboardingStep[];
  isOnboardingActive: boolean;
  completeStep: (stepId: string) => void;
  skipOnboarding: () => void;
  restartOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const defaultSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Platform Engineering Nexus',
    description:
      'Your visual platform for designing, validating, and managing cloud infrastructure. This quick tour will help you get started.',
    completed: false,
  },
  {
    id: 'create-project',
    title: 'Create a Project',
    description:
      'Head to the Design Canvas to create your first platform architecture project. Drag components onto the canvas to begin.',
    completed: false,
    href: '/canvas',
  },
  {
    id: 'design-architecture',
    title: 'Design Your Architecture',
    description:
      'Connect components, configure dependencies, and use the AI suggestions to refine your design. The canvas auto-validates as you build.',
    completed: false,
    href: '/canvas',
  },
  {
    id: 'run-validation',
    title: 'Run Validation',
    description:
      'Use the Validation Engine to check your architecture against best practices, security policies, and compliance requirements.',
    completed: false,
    href: '/validation',
  },
];

const STORAGE_KEY = 'pen_onboarding_progress';

const trackOnboardingEvent = (event: string, properties?: Record<string, unknown>) => {
  import('@/services/PostHogIntegrationService')
    .then(({ postHogService }) => {
      postHogService.trackEvent(event, properties);
    })
    .catch(() => {});
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<OnboardingStep[]>(defaultSteps);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  const saveProgress = useCallback((s: OnboardingStep[], step: number, active: boolean) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          steps: s.map(({ id, title, description, completed, href }) => ({
            id,
            title,
            description,
            completed,
            href,
          })),
          currentStep: step,
          isActive: active,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('Error saving onboarding progress:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        setSteps(data.steps || defaultSteps);
        setCurrentStep(data.currentStep || 0);
        setIsOnboardingActive(data.isActive !== false);
      } else if (user) {
        // First-time user — activate onboarding
        setIsOnboardingActive(true);
        saveProgress(defaultSteps, 0, true);
        trackOnboardingEvent('onboarding_started', { userId: user.id });
      }
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
    }
  }, [user, saveProgress]);

  const completeStep = (stepId: string) => {
    const updatedSteps = steps.map(step =>
      step.id === stepId ? { ...step, completed: true } : step
    );
    setSteps(updatedSteps);
    trackOnboardingEvent('onboarding_step_completed', { stepId });

    const completedStepIndex = steps.findIndex(step => step.id === stepId);
    const nextIncompleteStep = updatedSteps.findIndex(
      (step, index) => index > completedStepIndex && !step.completed
    );

    if (nextIncompleteStep !== -1) {
      setCurrentStep(nextIncompleteStep);
      saveProgress(updatedSteps, nextIncompleteStep, true);
    } else {
      setIsOnboardingActive(false);
      saveProgress(updatedSteps, completedStepIndex, false);
      trackOnboardingEvent('onboarding_completed', {
        stepsCompleted: updatedSteps.filter(s => s.completed).length,
      });
    }
  };

  const skipOnboarding = () => {
    setIsOnboardingActive(false);
    saveProgress(steps, currentStep, false);
    trackOnboardingEvent('onboarding_skipped', {
      skippedAtStep: steps[currentStep]?.id,
      stepsCompleted: steps.filter(s => s.completed).length,
    });
  };

  const restartOnboarding = () => {
    setSteps(defaultSteps);
    setCurrentStep(0);
    setIsOnboardingActive(true);
    saveProgress(defaultSteps, 0, true);
    trackOnboardingEvent('onboarding_restarted');
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      saveProgress(steps, next, isOnboardingActive);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      saveProgress(steps, prev, isOnboardingActive);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        currentStep,
        steps,
        isOnboardingActive,
        completeStep,
        skipOnboarding,
        restartOnboarding,
        nextStep,
        previousStep,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
